/* ENEA slider resync (2026-06-27)
 * Fixes the PDP gallery (Flickity) and the "Matches perfectly with" / upsell
 * carousels (Swiper) freezing after Safari is backgrounded and restored from
 * the back-forward cache. While hidden the slider can measure a zero width, so
 * its index/dots advance but the track never moves. On return to the foreground
 * (or bfcache restore, orientation change) we recalculate and re-snap.
 * Safe and idempotent: only runs on those events, no-ops if a library is absent.
 * To revert: delete this file and its <script> tag in snippets/loading.liquid.
 *
 * 2026-07-02: two fixes for "gallery stops swiping after cart add + remove".
 * On mobile the cart drawer covers the page; the scroll-lock/viewport change
 * can fire a resize while the gallery is covered, so Flickity measures a zero
 * width and freezes. We now also resync whenever the cart drawer closes
 * (body class js-drawer-open-cart removed). Also fixed the targeted Flickity
 * selector: it queried .flickity-enable (typo) so it never matched; the real
 * class is .flickity-enabled.
 *
 * 2026-07-18: root-cause rewrite for "gallery frozen after phone lock/unlock".
 * Two independent bugs made every previous version of this file a no-op:
 * 1. This theme never exposes window.Flickity. The library is bundled in
 *    vendor.js under window.themeVendor.Flickity, so the targeted resync
 *    branch below never ran, on any device, ever. We now resolve the
 *    constructor from either location.
 * 2. Vendor Flickity's resize() is guarded: it silently returns while
 *    isAnimating or isDragging is true. If iOS locks the screen mid-swipe or
 *    mid-settle, the pending animation frame is dropped and those flags stay
 *    stuck true forever. After that, startAnimation() never restarts (it
 *    early-returns when isAnimating is already true), so dragging moves the
 *    track under the finger but the gallery can never change slide again,
 *    and every resize (including our own dispatched one) hits the same guard
 *    and does nothing. On restore we now clear stuck drag/animation state
 *    before calling resize(), then re-select the current cell instantly
 *    (the instant path positions the track without needing the rAF loop).
 */
(function () {
  if (window.__eneaSliderResync) return; window.__eneaSliderResync = true;

  function flickityCtor() {
    if (window.Flickity && window.Flickity.data) return window.Flickity;
    if (window.themeVendor && window.themeVendor.Flickity && window.themeVendor.Flickity.data) return window.themeVendor.Flickity;
    return null;
  }

  // Clear the stuck-flag deadlock (see 2026-07-18 note), then re-measure and
  // re-snap. Only called from restore events, never during normal interaction,
  // so forcing the flags off here cannot interrupt a real drag.
  function recoverInstance(f) {
    try {
      if (f.isDragging) { f.isDragging = false; }
      if (f.isPointerDown) { f.isPointerDown = false; }
      if (f.isAnimating) { f.isAnimating = false; f.restingFrames = 0; }
      if (typeof f.resize === 'function') f.resize();
      if (typeof f.selectedIndex === 'number' && typeof f.select === 'function') {
        f.select(f.selectedIndex, false, true);
      }
    } catch (e) {}
  }

  function resync() {
    // Swiper: recalc each live instance and re-snap to its current slide
    try {
      document.querySelectorAll('.swiper').forEach(function (el) {
        var s = el.swiper;
        if (s && typeof s.update === 'function') {
          try {
            s.update();
            if (typeof s.activeIndex === 'number' && typeof s.slideTo === 'function') s.slideTo(s.activeIndex, 0, false);
          } catch (e) {}
        }
      });
    } catch (e) {}

    // Flickity: recover every live instance (PDP gallery, story cards, etc.)
    try {
      var F = flickityCtor();
      if (F) {
        document.querySelectorAll('.flickity-enabled, .product-gallery__media-slider').forEach(function (el) {
          var f = F.data(el);
          if (f && f.isActive) recoverInstance(f);
        });
      }
    } catch (e) {}

    ensurePairCarousel();

    // Flickity also auto-listens to window resize; this covers builds where the
    // instance is not reachable from a global constructor
    try { window.dispatchEvent(new Event('resize')); } catch (e) {}
  }

  // 2026-07-27b: if the pair-products carousel lost (or never got) its
  // Flickity instance, nothing in the theme rebuilds it after destroy (the
  // carousel--resize watcher dies with the instance) - the drawer then falls
  // back to native scroll with no dots. Recreate it ourselves, with the
  // options captured from a healthy live instance.
  function ensurePairCarousel() {
    try {
      var F = flickityCtor(); if (!F) return;
      document.querySelectorAll('#cart-drawer [data-pair-products]').forEach(function (el) {
        if (el.classList.contains('flickity-enabled')) return;
        if (!el.children.length) return;
        new F(el, { pageDots: true, prevNextButtons: false, wrapAround: true, cellAlign: 'center', adaptiveHeight: true, draggable: '>1', accessibility: true });
      });
    } catch (e) {}
  }

  var t;
  function schedule(delay) { clearTimeout(t); t = setTimeout(resync, delay || 80); }

  // Restore events run the resync twice: once right away and once after the
  // unlock/restore settles, because iOS can still report a transient zero
  // layout during the unlock animation.
  function scheduleRestore() { schedule(80); setTimeout(resync, 700); }

  // bfcache restore (Safari): the key trigger
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleRestore(); });
  // returning to the tab/app, including screen unlock on the same page
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') scheduleRestore(); });
  window.addEventListener('focus', function () { schedule(120); });
  // rotating the phone while away
  window.addEventListener('orientationchange', function () { setTimeout(resync, 300); });

  // cart drawer closed (2026-07-02): the drawer covered the page, and a resize
  // while covered leaves Flickity measured at zero width - re-measure now.
  // Double-tap the resync because the drawer close animation runs ~280ms.
  try {
    // 2026-07-27: also resync when the drawer OPENS - in some sessions the
    // pair carousel's Flickity is skipped/destroyed after a zero-width
    // measurement (no dots, native scroll, macOS painted a permanent fat
    // scrollbar). A resize nudge on open lets the theme re-evaluate and
    // re-init now that the carousel is visible and measurable.
    var wasOpen = document.body.classList.contains('js-drawer-open-cart');
    new MutationObserver(function () {
      var open = document.body.classList.contains('js-drawer-open-cart');
      if (wasOpen && !open) { schedule(120); setTimeout(resync, 500); }
      if (!wasOpen && open) { schedule(150); setTimeout(resync, 600); setTimeout(ensurePairCarousel, 750); }
      wasOpen = open;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  } catch (e) {}

  // 2026-07-26: mid-session watchdog. The restore-event fixes above never run
  // while the page stays visible, but the same stuck-flag deadlock can happen
  // mid-session (a vertical-swipe cancellation swallowing the pointerup that
  // Flickity needed to end its drag). Signature: no finger on the screen yet
  // drag/animation flags still true across two consecutive ticks. Legit
  // settles finish well under one tick, so two ticks with no touch = wedged.
  var eneaTouchDown = 0;
  document.addEventListener('touchstart', function () { eneaTouchDown++; }, { capture: true, passive: true });
  function eneaTouchUp() { eneaTouchDown = Math.max(0, eneaTouchDown - 1); }
  document.addEventListener('touchend', eneaTouchUp, { capture: true, passive: true });
  document.addEventListener('touchcancel', eneaTouchUp, { capture: true, passive: true });
  var eneaSuspect = (typeof WeakMap === 'function') ? new WeakMap() : null;
  setInterval(function () {
    try {
      if (eneaTouchDown > 0 || !eneaSuspect) return;
      var F = flickityCtor();
      if (!F) return;
      // 2026-07-27: watchdog scoped to the cart drawer only. It was built for
      // the drawer pair-carousel deadlock; monitoring the PDP gallery risked
      // interfering with its longest animation (the wrapAround last-to-first
      // pass), and the gallery already has its own restore-event recovery.
      document.querySelectorAll('#cart-drawer .flickity-enabled').forEach(function (el) {
        var f = F.data(el);
        if (!f || !f.isActive) return;
        // 2026-07-27: count ticks instead of a boolean so a slow settle
        // animation is never snapped mid-flight. A stuck pointer/drag
        // recovers after 2 ticks (~2.4s); an animation-only state needs 4
        // ticks (~4.8s) - real settles finish well under one second, real
        // deadlocks persist forever.
        var hardStuck = !!(f.isPointerDown || f.isDragging);
        var softStuck = !!f.isAnimating;
        var n = eneaSuspect.get(f) || 0;
        if (hardStuck) { n += 1; } else if (softStuck) { n += 0.5; } else { n = 0; }
        if (n >= 2) { eneaSuspect.set(f, 0); recoverInstance(f); }
        else { eneaSuspect.set(f, n); }
      });
    } catch (e) {}
  }, 1200);
})();
